import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authenticatedFetch } from './api';
import { getReportPreview } from './services/freemiumApi';
import FastTrackUpsellModal from './components/FastTrackUpsellModal';
import './ReportPreviewPage.css';

const API_BASE = process.env.REACT_APP_API_BASE || "https://mockbot-api1.onrender.com";
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;

/**
 * ReportPreviewPage — Freemium Conversion Funnel.
 *
 * Polls the existing, unmodified GET /evaluation_status/:sessionId until the
 * real evaluation completes, then fetches the redacted preview from the new
 * GET /api/freemium/report-preview/:sessionId endpoint. No evaluation logic
 * lives here — this page only displays what those two endpoints return.
 */
export default function ReportPreviewPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('polling'); // polling | ready | error | timeout
  const [report, setReport] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const pollStartRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    pollStartRef.current = Date.now();

    const poll = async () => {
      if (cancelled) return;

      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        setPhase('timeout');
        return;
      }

      try {
        const res = await authenticatedFetch(`${API_BASE}/evaluation_status/${sessionId}`);
        const data = await res.json();

        if (data?.status === 'COMPLETED') {
          const preview = await getReportPreview(sessionId);
          if (cancelled) return;
          setReport(preview);
          setPhase('ready');
          return;
        }

        if (data?.status === 'FAILED') {
          if (cancelled) return;
          setErrorMsg(data.message || 'Evaluation failed. Please contact support.');
          setPhase('error');
          return;
        }
      } catch (err) {
        console.error('Report preview poll error:', err);
      }

      if (!cancelled) {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (phase === 'polling') {
    return (
      <div className="rpp-container">
        <div className="rpp-card rpp-loading">
          <div className="rpp-spinner" />
          <h2>Analyzing your interview…</h2>
          <p>This usually takes under a minute. Hang tight while we generate your report.</p>
        </div>
      </div>
    );
  }

  if (phase === 'timeout' || phase === 'error') {
    return (
      <div className="rpp-container">
        <div className="rpp-card rpp-loading">
          <h2>Report is taking longer than expected</h2>
          <p>{errorMsg || "We're still processing your interview. Please check your dashboard again shortly."}</p>
          <button className="rpp-btn rpp-btn-secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const strength = report?.strengths?.[0];
  const weakness = report?.weaknesses?.[0];

  return (
    <div className="rpp-container">
      <div className="rpp-card">
        <div className="rpp-header">
          <h1>Your Interview Report</h1>
          <p>Here's a preview of how you did. Unlock the full breakdown to see everything.</p>
        </div>

        <div className="rpp-scores">
          <div className="rpp-score-tile">
            <span className="rpp-score-value">{report?.overall_score ?? '—'}</span>
            <span className="rpp-score-label">Overall Score</span>
          </div>
          <div className="rpp-score-tile">
            <span className="rpp-score-value">{report?.communication_score ?? '—'}</span>
            <span className="rpp-score-label">Communication</span>
          </div>
          <div className="rpp-score-tile">
            <span className="rpp-score-value">{report?.confidence_score ?? '—'}</span>
            <span className="rpp-score-label">Confidence</span>
          </div>
        </div>

        <div className="rpp-highlights">
          {strength && (
            <div className="rpp-highlight rpp-highlight-strength">
              <span className="rpp-highlight-tag">Strength</span>
              <p>{strength}</p>
            </div>
          )}
          {weakness && (
            <div className="rpp-highlight rpp-highlight-weakness">
              <span className="rpp-highlight-tag">Improvement Area</span>
              <p>{weakness}</p>
            </div>
          )}
        </div>

        <div className="rpp-locked-section">
          <div className="rpp-locked-blur" aria-hidden="true">
            <div className="rpp-locked-row" />
            <div className="rpp-locked-row" />
            <div className="rpp-locked-row" />
            <div className="rpp-locked-row" />
          </div>
          <div className="rpp-locked-overlay">
            <span className="rpp-lock-icon">🔒</span>
            <h3>Unlock Full Placement Readiness</h3>
            <p>
              Detailed technical analysis, full recommendations, question-by-question breakdown,
              transcript, and downloadable PDF report — all included with an upgrade.
            </p>
            <button className="rpp-btn rpp-btn-primary" onClick={() => setModalOpen(true)}>
              Upgrade to Unlock Full Report
            </button>
          </div>
        </div>
      </div>
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => navigate('/dashboard')}
      />
    </div>
  );
}
